import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';

import eqHelper from 'webapp/helpers/eq';
import CommonUiIcon from 'webapp/components/common/ui-icon';
import OnboardingGuidedTourSteps from 'webapp/components/onboarding/guided-tour/steps';
import SubmitButton from 'webapp/components/submit-button';

import { BannerBottomType, type StepTourData } from 'webapp/services/flow/tour/onboarding';

interface OnboardingGuidedTourBannerComponentInterface {
	Element: HTMLDivElement;
	Args: object;
}

export default class OnboardingGuidedTourBannerComponent extends Component<OnboardingGuidedTourBannerComponentInterface> {
	@service('flow/tour/onboarding')
	declare flowTourOnboardingService: Service.Flow.Tour.Onboarding;
	@service('navigation') declare navigationService: Service.Navigation;

	get currentStepTourBannerData(): StepTourData | null {
		return this.flowTourOnboardingService.currentStepTourStepData;
	}

	get bannerText() {
		return this.currentStepTourBannerData?.text || '';
	}

	get continueBtnTooltipText(): string {
		return this.currentStepTourBannerData?.tooltipText || '';
	}

	get minSelectedMetricsCount(): number {
		return this.flowTourOnboardingService.minSelectedMetricsCount;
	}

	get isContinueBtnDisabled(): boolean {
		const { currentStepTourStepData, selectedMetricsCount } = this.flowTourOnboardingService;

		return (
			(this.currentStepTourBannerData?.type !== BannerBottomType.METRICS_COUNT &&
				!currentStepTourStepData?.isContinueBtnEnabled) ||
			(this.currentStepTourBannerData?.type === BannerBottomType.METRICS_COUNT &&
				!selectedMetricsCount)
		);
	}

	get selectedMetricsCount(): number {
		return Math.min(
			this.flowTourOnboardingService.selectedMetricsCount,
			this.minSelectedMetricsCount,
		);
	}

	get selectedMetricsProgressSteps() {
		return Array(this.minSelectedMetricsCount)
			.fill(null)
			.map((_item, idx) => {
				return {
					marked: idx + 1 <= this.flowTourOnboardingService.selectedMetricsCount,
				};
			});
	}

	get isCurrentStepMetricsCount() {
		return this.currentStepTourBannerData?.type === BannerBottomType.METRICS_COUNT;
	}

	@action
	continue(): void {
		if (this.isContinueBtnDisabled) {
			return;
		}

		this.flowTourOnboardingService.continueTour();
	}

	@action
	close(): void {
		this.flowTourOnboardingService.openOnboardingTourExitWarningPopup();
	}

	<template>
		<div class="onboarding-guided-tour-banner">
			<div class="onboarding-guided-tour-banner__top">
				<strong>Guided tour</strong>
				<OnboardingGuidedTourSteps />
				<a role="button" class="close" {{on "click" this.close}}>
					<CommonUiIcon @name="Close" @size="sm" />
				</a>
			</div>
			{{#unless (eqHelper this.currentStepTourBannerData.type BannerBottomType.NONE)}}
				<div class="onboarding-guided-tour-banner__bottom">
					<div class="content">
						{{#if (eqHelper this.currentStepTourBannerData.type BannerBottomType.TEXT)}}
							<strong>{{{this.bannerText}}}</strong>
						{{else if this.isCurrentStepMetricsCount}}
							<div class="progress">
								<div class="progress__status">
									<strong>Add metrics</strong>
									{{this.selectedMetricsCount}}
									/
									{{this.minSelectedMetricsCount}}
								</div>
								<div class="progress__bar">
									<ul>
										{{#each this.selectedMetricsProgressSteps as |step|}}
											<li class={{if step.marked "marked"}}></li>
										{{/each}}
									</ul>
								</div>
							</div>
						{{/if}}
					</div>

					<SubmitButton
						@text="Continue"
						@extraClass="continue"
						@noDelay={{true}}
						@isDisabled={{this.isContinueBtnDisabled}}
						@tooltipText={{this.continueBtnTooltipText}}
						@onTrigger={{this.continue}}
					/>
				</div>
			{{/unless}}

		</div>
	</template>
}
